import { useEffect, useState } from "react";
import TableComponent, { type TableColumn } from "@/app/components/ui/Reusable/TableComponent";
import { useClinicToDoctorRequestActionAPI } from "@/app/hooks/api_hooks/Inventory/ClinicToDoctorTransfer/useClinicToDoctorRequestActionAPI";
import {
  IClinicToDoctorItem,
  IClinicToDoctorRequestItem,
} from "@/app/hooks/api_hooks/Inventory/ClinicToDoctorTransfer/useClinicToDoctorRequestListAPI";
import { AHS_CLICK_EVENTS, useAhsInventoryClickLog } from "@/app/hooks/useAhsInventoryClickLog";

const TABLE_CELL_CLASS = "text-[0.75rem] font-normal text-black";
const TABLE_CELL_MEDIUM_CLASS = "text-[0.75rem] font-medium text-black";
const TABLE_HEADER_CLASS = "text-[0.875rem] font-semibold text-[#193A6A]";

interface IViewItemsModalProps {
  open: boolean;
  onClose: () => void;
  item: IClinicToDoctorRequestItem | null;
  direction?: string;
  clinicId?: string;
  onActionDone?: () => void;
}

const viewItemsColumns: TableColumn<IClinicToDoctorItem>[] = [
  {
    id: "index",
    header: "#",
    width: 48,
    className: TABLE_CELL_CLASS,
    headerClassName: TABLE_HEADER_CLASS,
    cell: ({ rowIndex }) => rowIndex + 1,
  },
  {
    id: "name",
    header: "Name",
    className: TABLE_CELL_MEDIUM_CLASS,
    headerClassName: TABLE_HEADER_CLASS,
    cell: ({ row }) => row.name ?? "—",
  },
  {
    id: "qty",
    header: "Qty",
    className: TABLE_CELL_CLASS,
    headerClassName: TABLE_HEADER_CLASS,
    cell: ({ row }) => (
      <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-medium text-[0.75rem]">{row.qty ?? 0}</span>
    ),
  },
  {
    id: "lot_number",
    header: "Lot No.",
    className: TABLE_CELL_CLASS,
    headerClassName: TABLE_HEADER_CLASS,
    cell: ({ row }) => row.lot_details?.lot_number ?? "—",
  },
  {
    id: "expiry",
    header: "Expiry",
    className: TABLE_CELL_CLASS,
    headerClassName: TABLE_HEADER_CLASS,
    cell: ({ row }) => row.lot_details?.expiry_date ?? "—",
  },
  {
    id: "mrp",
    header: "MRP",
    className: TABLE_CELL_CLASS,
    headerClassName: TABLE_HEADER_CLASS,
    cell: ({ row }) => (row.lot_details?.mrp != null ? `₹${row.lot_details.mrp}` : "—"),
  },
];

const ViewItemsModal = ({ open, onClose, item, direction, clinicId, onActionDone }: IViewItemsModalProps) => {
  const [confirmAction, setConfirmAction] = useState<"accept" | "reject" | null>(null);
  const { execute: submitAction, isSubmitting: isActing } = useClinicToDoctorRequestActionAPI();
  const { logAhsEvent } = useAhsInventoryClickLog();

  const requestLogFields = {
    direction,
    request_id: item?.request_id,
    request_number: item?.request_number,
    items_count: item?.items?.length ?? 0,
  };

  const handleActionSelect = (action: "accept" | "reject") => {
    logAhsEvent(
      action === "accept" ? AHS_CLICK_EVENTS.ACCEPT_REQUEST_CLICK : AHS_CLICK_EVENTS.REJECT_REQUEST_CLICK,
      requestLogFields,
    );
    setConfirmAction(action);
  };

  useEffect(() => {
    if (!open) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [open]);

  if (!open || !item) return null;

  const showActions = direction === "doctor_to_clinic" && item.action_buttons?.includes("view_request");

  const handleConfirm = async () => {
    if (!confirmAction || !item || !clinicId) return;
    logAhsEvent(AHS_CLICK_EVENTS.REQUEST_ACTION_CONFIRM_CLICK, {
      event_action: confirmAction,
      ...requestLogFields,
    });
    const { status } = await submitAction(item.request_id, confirmAction, { clinic_id: clinicId });
    setConfirmAction(null);
    if (status) {
      onClose();
      onActionDone?.();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <p className="text-sm font-semibold text-gray-900">Items in Request</p>
            <p className="text-xs text-gray-400 mt-0.5">{item.request_number}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-lg leading-none cursor-pointer"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 max-h-[60vh] overflow-y-auto">
          {item.items?.length > 0 ? (
            <TableComponent
              columns={viewItemsColumns}
              data={item.items}
              getRowId={(row, index) => row.unique_row_id ?? index}
              scrollContainerClassName="mt-0 max-h-[50vh] overflow-y-auto overflow-x-auto"
              minTableWidthClassName="min-w-full"
              tableClassName="rounded-[20px]"
              bodyClassName="text-xs"
            />
          ) : (
            <p className="text-xs text-gray-400 text-center py-6">No items found.</p>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2">
          {showActions ? (
            <>
              <button
                onClick={() => handleActionSelect("reject")}
                className="px-4 py-2 rounded-md border border-red-300 text-red-600 text-xs font-medium cursor-pointer hover:bg-red-50"
              >
                Reject
              </button>
              <button
                onClick={() => handleActionSelect("accept")}
                className="px-4 py-2 rounded-md bg-black text-white text-xs font-medium cursor-pointer hover:bg-gray-800"
              >
                Accept
              </button>
            </>
          ) : (
            <button
              onClick={onClose}
              className="bg-[#193A6A] text-white text-xs font-medium px-4 py-2 rounded-md cursor-pointer hover:bg-[#14305a] transition-colors"
            >
              Close
            </button>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmAction && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40" onClick={() => setConfirmAction(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4">
              <p className="text-sm text-gray-900">
                Are you sure you want to <span className="font-semibold capitalize">{confirmAction}</span> this request?
              </p>
            </div>
            <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2">
              <button
                onClick={() => setConfirmAction(null)}
                className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 text-xs font-medium cursor-pointer hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                disabled={isActing}
                onClick={handleConfirm}
                className={`px-4 py-2 rounded-md text-white text-xs font-medium cursor-pointer disabled:opacity-50 ${
                  confirmAction === "accept" ? "bg-black hover:bg-gray-800" : "bg-red-600 hover:bg-red-700"
                }`}
              >
                {isActing ? "Processing..." : `Confirm ${confirmAction === "accept" ? "Accept" : "Reject"}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewItemsModal;
